import { useState, useEffect, useRef } from 'react';
import TRTC from 'trtc-sdk-v5';
import axios from 'axios';
import { getHostIP, getVaccineTypeFromHostname } from '../../networking/Hosts';
import WebAudioSpeechRecognizer from '../../app/webaudiospeechrecognizer.js';
import styles from './VoiceCall.module.css';

const VoiceCall = () => {
  const hostIP = getHostIP();
  
  // localStorage key
  const STORAGE_KEY = 'voicecall_queue_state';
  
  // 用户信息
  const [userId, setUserId] = useState('');
  const sdkAppId = 1600121889;
  const [userSig, setUserSig] = useState('');
  
  // 通话状态
  const [isInRoom, setIsInRoom] = useState(false);
  const [roomId, setRoomId] = useState(null);
  const [queueStatus, setQueueStatus] = useState(''); // 'waiting', 'ready', ''
  const [error, setError] = useState('');
  const [queueId, setQueueId] = useState(null); // 保存 queueId 用于恢复
  
  // 远端用户列表
  const [remoteUsers, setRemoteUsers] = useState([]);
  
  // 字幕/翻译消息列表
  const [subtitleMessages, setSubtitleMessages] = useState([]);
  // 聊天消息相关状态
  const [chatMessage, setChatMessage] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [displayedMessage, setDisplayedMessage] = useState('');
  
  // 键盘状态
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const chatInputRef = useRef(null);
  
  // 语音识别相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [recognizedText, setRecognizedText] = useState('');
  const [recordingStatus, setRecordingStatus] = useState(''); // 'connecting', 'recording', ''
  const webAudioSpeechRecognizerRef = useRef(null);
  const [tencentCredentials, setTencentCredentials] = useState(null);
  const recordingStartTimeRef = useRef(null); // 记录录音开始时间
  const [isAudioMuted, setIsAudioMuted] = useState(false); // 音频静音状态
  
  // 使用条款状态
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  
  // 倒计时状态（5分钟 = 300秒）
  const [countdown, setCountdown] = useState(300); // 5分钟
  const countdownTimerRef = useRef(null);
  
  const chatType = getVaccineTypeFromHostname();
  
  // TRTC 实例引用
  const trtcRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const pollTimerRef = useRef(null); // 保存轮询定时器引用
  const subtitleMapRef = useRef({}); // 用于管理实时字幕更新（key: roundid 或 sender+seq）
  const chatEndRef = useRef(null); // 用于滚动到聊天记录底部

  // 检测键盘弹出/收起
  useEffect(() => {
    if (!isInRoom) return;

    let initialViewportHeight = window.innerHeight;
    let initialViewportTop = window.visualViewport?.offsetTop || 0;

    const handleResize = () => {
      if (window.visualViewport) {
        const currentViewportHeight = window.visualViewport.height;
        const currentViewportTop = window.visualViewport.offsetTop;
        const windowHeight = window.innerHeight;
        
        // 计算键盘高度：窗口高度 - 可视视口高度 - 视口偏移
        const calculatedKeyboardHeight = windowHeight - currentViewportHeight - currentViewportTop;
        
        // 如果键盘高度超过100px，认为键盘弹出
        if (calculatedKeyboardHeight > 100) {
          setIsKeyboardVisible(true);
          setKeyboardHeight(calculatedKeyboardHeight);
        } else {
          setIsKeyboardVisible(false);
          setKeyboardHeight(0);
        }
      } else {
        // 降级方案：使用窗口高度差
        const currentViewportHeight = window.innerHeight;
        const heightDiff = initialViewportHeight - currentViewportHeight;
        
        if (heightDiff > 150) {
          setIsKeyboardVisible(true);
          setKeyboardHeight(heightDiff);
        } else {
          setIsKeyboardVisible(false);
          setKeyboardHeight(0);
        }
      }
    };

    const handleFocus = () => {
      // 输入框聚焦时，立即检测键盘
      setTimeout(() => {
        handleResize();
      }, 100);
    };

    const handleBlur = () => {
      // 输入框失焦时，延迟检测（给键盘收起时间）
      setTimeout(() => {
        setIsKeyboardVisible(false);
        setKeyboardHeight(0);
        // 更新初始高度
        initialViewportHeight = window.innerHeight;
        initialViewportTop = window.visualViewport?.offsetTop || 0;
      }, 300);
    };

    // 使用 visualViewport API（移动端更准确）
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    // 监听输入框焦点事件
    const inputElement = chatInputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
    };
  }, [isInRoom]);

  // 聊天消息更新时自动滚动到底部
  useEffect(() => {
    if (chatEndRef.current && !isKeyboardVisible) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatLog, displayedMessage, isKeyboardVisible]);

  // 键盘弹出时，确保输入框可见
  useEffect(() => {
    if (isKeyboardVisible && chatInputRef.current && keyboardHeight > 0) {
      // 延迟一下，等待键盘完全弹出和位置更新
      const timer1 = setTimeout(() => {
        if (chatInputRef.current) {
          const inputRect = chatInputRef.current.getBoundingClientRect();
          const viewportHeight = window.visualViewport?.height || window.innerHeight;
          const viewportTop = window.visualViewport?.offsetTop || 0;
          
          // 计算输入框相对于可视视口的位置
          const inputBottom = inputRect.bottom - viewportTop;
          
          // 如果输入框在可视区域下方，滚动到可见
          if (inputBottom > viewportHeight - 20) {
            // 使用 scrollIntoView 滚动
            chatInputRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'end',
              inline: 'nearest'
            });
            
            // 备用方案：使用 window.scrollTo
            setTimeout(() => {
              const newRect = chatInputRef.current?.getBoundingClientRect();
              if (newRect) {
                const scrollY = window.scrollY + (newRect.bottom - viewportHeight + 20);
                window.scrollTo({
                  top: scrollY,
                  behavior: 'smooth'
                });
              }
            }, 100);
          }
        }
      }, 300);
      
      return () => {
        clearTimeout(timer1);
      };
    }
  }, [isKeyboardVisible, keyboardHeight]);

  // 恢复排队状态（页面刷新后）
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        // 检查状态是否过期（超过30分钟认为过期）
        const now = Date.now();
        if (state.timestamp && (now - state.timestamp < 30 * 60 * 1000)) {
          setUserId(state.userId || '');
          setQueueId(state.queueId || null);
          
          // 如果之前是在排队中，恢复排队状态
          if (state.queueStatus === 'waiting' && state.queueId) {
            setQueueStatus('waiting');
            // 继续轮询
            pollCallStatus(state.queueId);
          }
        } else {
          // 状态过期，清除
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('恢复排队状态失败:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // 保存排队状态到 localStorage
  const saveQueueState = (state) => {
    const stateToSave = {
      ...state,
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  };

  // 清除排队状态
  const clearQueueState = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  // 获取腾讯云临时密钥
  const getTencentToken = async () => {
    try {
      const response = await axios.get(`${hostIP}/api/tencent/token`, {
        withCredentials: true
      });
      if (response.data.success) {
        const tokenData = {
          ...response.data.data,
          appid: 1328869042
        };
        setTencentCredentials(tokenData);
        return tokenData;
      }
    } catch (error) {
      console.error('获取腾讯云token失败:', error);
      setError('获取语音识别权限失败，请重试。');
    }
    return null;
  };

  // 初始化 TRTC 实例
  useEffect(() => {
    if (!trtcRef.current) {
      trtcRef.current = TRTC.create();
      setupEventListeners();
    }
    
    return () => {
      // 组件卸载时清理
      stopPolling(); // 停止轮询
      stopCountdown(); // 停止倒计时
      // 确保释放麦克风资源
      if (webAudioSpeechRecognizerRef.current) {
        webAudioSpeechRecognizerRef.current.stop();
        webAudioSpeechRecognizerRef.current.destroyStream();
        webAudioSpeechRecognizerRef.current = null;
      }
      if (trtcRef.current) {
        trtcRef.current.destroy();
        trtcRef.current = null;
      }
    };
  }, []);

  // 设置事件监听器
  const setupEventListeners = () => {
    const trtc = trtcRef.current;
    if (!trtc) return;

    // 监听远端视频可用事件
    trtc.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, ({ userId: remoteUserId, streamType }) => {
      console.log('远端视频可用:', remoteUserId, streamType);
      const viewId = `${remoteUserId}_${streamType}`;
      
      // 创建视频容器
      if (!remoteVideoRefs.current[viewId]) {
        const videoContainer = document.createElement('div');
        videoContainer.id = viewId;
        videoContainer.className = styles.remoteVideoContainer;
        document.getElementById('remote-videos-container').appendChild(videoContainer);
        remoteVideoRefs.current[viewId] = videoContainer;
      }
      
      // 播放远端视频
      trtc.startRemoteVideo({ userId: remoteUserId, streamType, view: viewId });
      
      // 更新远端用户列表
      setRemoteUsers(prev => {
        const exists = prev.find(u => u.userId === remoteUserId && u.streamType === streamType);
        if (!exists) {
          return [...prev, { userId: remoteUserId, streamType, viewId }];
        }
        return prev;
      });
    });

    // 监听远端视频不可用事件
    trtc.on(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, ({ userId: remoteUserId, streamType }) => {
      console.log('远端视频不可用:', remoteUserId, streamType);
      const viewId = `${remoteUserId}_${streamType}`;
      
      // 移除视频容器
      if (remoteVideoRefs.current[viewId]) {
        const container = remoteVideoRefs.current[viewId];
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
        delete remoteVideoRefs.current[viewId];
      }
      
      // 更新远端用户列表
      setRemoteUsers(prev => prev.filter(u => !(u.userId === remoteUserId && u.streamType === streamType)));
    });

    // 监听远端音频可用事件
    trtc.on(TRTC.EVENT.REMOTE_AUDIO_AVAILABLE, ({ userId: remoteUserId }) => {
      console.log('远端音频可用:', remoteUserId);
      // 默认自动播放，如果需要手动控制，可以在这里处理
    });

    // 监听被移出房间事件
    trtc.on(TRTC.EVENT.KICKED_OUT, (error) => {
      console.error('被移出房间:', error);
      setError(`被移出房间: ${error.reason} - ${error.message}`);
      handleExitRoom();
    });

    // 监听错误事件
    trtc.on(TRTC.EVENT.ERROR, (error) => {
      console.error('TRTC 错误:', error);
      setError(`TRTC 错误: ${error.message || error}`);
    });

    // 监听自定义消息（语音识别结果）
    trtc.on(TRTC.EVENT.CUSTOM_MESSAGE, (event) => {
      try {
        // event.userId: 语音识别机器人的userId
        // event.cmdId: 消息Id, 转录和字幕固定为1
        // event.seq: 消息的序号
        // event.data: ArrayBuffer 类型，转录或字幕的内容
        const data = new TextDecoder().decode(event.data);
        console.log(`收到自定义消息 from ${event.userId}, message: ${data}`);

        // 解析 JSON 数据
        const messageData = JSON.parse(data);
        
        // 只处理 type 为 10000 的消息（实时字幕和翻译）
        if (messageData.type === 10000) {
          handleSubtitleMessage(messageData, event.userId, event.seq);
        }
      } catch (error) {
        console.error('解析自定义消息失败:', error);
      }
    });
  };

  // 处理字幕/翻译消息
  const handleSubtitleMessage = (messageData, senderUserId, seq) => {
    const { sender, payload } = messageData;
    
    // 判断是字幕还是翻译消息
    if (payload.translation_text) {
      // 翻译消息
      handleTranslationMessage(messageData, senderUserId, seq);
    } else if (payload.text !== undefined) {
      // 字幕消息
      handleSubtitleTextMessage(messageData, senderUserId, seq);
    }
  };

  // 处理字幕文本消息
  const handleSubtitleTextMessage = (messageData, senderUserId, seq) => {
    const { sender, payload } = messageData;
    const key = `${sender}_${seq}`;
    
    const subtitleItem = {
      id: key,
      sender: sender || senderUserId,
      text: payload.text || '',
      startTime: payload.start_time || '',
      endTime: payload.end_time || '',
      isComplete: payload.end === true,
      timestamp: Date.now(),
      type: 'subtitle'
    };

    // 如果是完整句子，添加到列表
    if (payload.end === true) {
      setSubtitleMessages(prev => {
        // 移除之前未完成的相同发送者的消息
        const filtered = prev.filter(msg => !(msg.sender === subtitleItem.sender && !msg.isComplete));
        return [...filtered, subtitleItem];
      });
      // 清除临时消息
      delete subtitleMapRef.current[key];
    } else {
      // 实时更新，替换之前的片段
      subtitleMapRef.current[key] = subtitleItem;
      // 更新显示（可以显示最新的实时字幕）
      setSubtitleMessages(prev => {
        const filtered = prev.filter(msg => msg.id !== key);
        return [...filtered, subtitleItem];
      });
    }
  };

  // 处理翻译消息
  const handleTranslationMessage = (messageData, senderUserId, seq) => {
    const { sender, payload } = messageData;
    const roundid = payload.roundid || `${sender}_${seq}`;
    
    const translationItem = {
      id: roundid,
      sender: sender || senderUserId,
      text: payload.translation_text || '',
      startTimeMs: payload.start_time_ms || 0,
      endTimeMs: payload.end_time_ms || 0,
      isComplete: payload.end === true,
      language: payload.translation_language || '',
      taskId: payload.taskid || '',
      timestamp: Date.now(),
      type: 'translation'
    };

    // 更新或添加翻译消息
    setSubtitleMessages(prev => {
      const existingIndex = prev.findIndex(msg => msg.id === roundid);
      if (existingIndex >= 0) {
        // 更新现有消息
        const updated = [...prev];
        updated[existingIndex] = translationItem;
        return updated;
      } else {
        // 添加新消息
        return [...prev, translationItem];
      }
    });
  };

  // 实际开始通话的逻辑
  const doStartCall = async () => {
    if (sdkAppId === 0) {
      setError('请先配置 SDKAppID');
      return;
    }

    try {
      setError('');
      setQueueStatus('waiting');
      
      // 调用开始通话接口
      const response = await axios.post(`${hostIP}/api/voicecall/start`, {
        sdkAppId: sdkAppId
      }, {
        withCredentials: true
      });

      const data = response.data;
      
      if (data.status === 'queuing') {
        // 排队中，需要轮询
        const currentQueueId = data.queueId || data.sessionId;
        setUserId(data.credential);
        setQueueId(currentQueueId);
        setQueueStatus('waiting');
        // 保存排队状态
        saveQueueState({
          sdkAppId,
          queueId: currentQueueId,
          queueStatus: 'waiting'
        });
        pollCallStatus(currentQueueId);
      } else if (data.status === 'ready' && data.roomId) {
        // 直接返回 roomId，可以进入房间
        setUserId(data.credential);
        setRoomId(data.roomId);
        setUserSig(data.userSig || '');
        setQueueStatus('ready');
        // 清除排队状态
        clearQueueState();
        await enterRoom(data.roomId, data.userSig, data.credential);
      } else {
        setError('服务器返回异常: ' + (data.message || '未知错误'));
        setQueueStatus('');
        clearQueueState();
      }
    } catch (error) {
      console.error('开始通话失败:', error);
      setError(`开始通话失败: ${error.response?.data?.message || error.message || '网络错误'}`);
      setQueueStatus('');
      clearQueueState();
    }
  };

  // 调用开始通话接口
  const startCall = async () => {
    // 如果未同意条款，不允许开始通话
    if (!hasAcceptedTerms) {
      setError('请先阅读并同意使用条款');
      return;
    }
    
    // 已同意条款，开始通话
    await doStartCall();
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // 轮询通话状态
  const pollCallStatus = async (queueId) => {
    const maxAttempts = 60; // 最多轮询60次
    let attempts = 0;

    const poll = async () => {
      // 检查是否已取消排队
      if (!pollTimerRef.current) {
        return; // 已取消，停止轮询
      }

      if (attempts >= maxAttempts) {
        setError('排队超时，请重试');
        setQueueStatus('');
        clearQueueState();
        stopPolling();
        return;
      }

      try {
        const response = await axios.get(`${hostIP}/api/voicecall/status`, {
          params: { queueId },
          withCredentials: true
        });

        const data = response.data;
        
        if (data.status === 'ready' && data.roomId) {
          // 准备就绪，可以进入房间
          stopPolling();
          setRoomId(data.roomId);
          setUserSig(data.userSig || '');
          setQueueStatus('ready');
          // 清除排队状态
          clearQueueState();
          await enterRoom(data.roomId, data.userSig);
        } else if (data.status === 'queuing') {
          // 继续排队，更新保存的状态
          saveQueueState({
            userId,
            sdkAppId,
            queueId,
            queueStatus: 'waiting'
          });
          attempts++;
          pollTimerRef.current = setTimeout(poll, 1000); // 1秒后再次轮询
        } else {
          setError('排队状态异常: ' + (data.message || '未知错误'));
          setQueueStatus('');
          clearQueueState();
          stopPolling();
        }
      } catch (error) {
        console.error('轮询状态失败:', error);
        attempts++;
        if (attempts < maxAttempts && pollTimerRef.current) {
          pollTimerRef.current = setTimeout(poll, 1000);
        } else {
          setError('轮询超时，请重试');
          setQueueStatus('');
          clearQueueState();
          stopPolling();
        }
      }
    };

    // 开始第一次轮询
    poll();
  };

  // 进入房间
  const enterRoom = async (roomIdValue, userSigValue, userIdValue) => {
    const trtc = trtcRef.current;
    if (!trtc) {
      setError('TRTC 实例未初始化');
      return;
    }

    try {
      await trtc.enterRoom({
        sdkAppId,
        userId: userIdValue || userId,
        userSig: userSigValue || userSig,
        roomId: roomIdValue || roomId,
        autoReceiveAudio: true, // 自动接收音频
      });
      
      console.log('进入房间成功');
      setIsInRoom(true);
      setError('');
      // 启动倒计时
      setCountdown(300);
      startCountdown();
      // 预先获取腾讯云token
      getTencentToken();
    } catch (error) {
      console.error('进入房间失败:', error);
      setError(`进入房间失败: ${error.message || error}`);
      setIsInRoom(false);
    }
  };

  // 静音/取消静音TRTC音频
  const muteLocalAudio = async (mute) => {
    const trtc = trtcRef.current;
    if (!trtc || !isInRoom) return;

    try {
      await trtc.muteLocalAudio(mute);
      setIsAudioMuted(mute);
      console.log(mute ? '音频已静音' : '音频已取消静音');
    } catch (error) {
      console.error('设置音频静音失败:', error);
    }
  };

  // 开始语音识别
  const startVoiceRecognition = async () => {
    if (!WebAudioSpeechRecognizer) {
      setError('语音识别SDK未加载');
      return;
    }

    if (!isInRoom) {
      setError('请先进入房间');
      return;
    }

    let credentials = tencentCredentials;
    if (!credentials) {
      credentials = await getTencentToken();
      if (!credentials) {
        return;
      }
    }

    // 静音TRTC音频
    await muteLocalAudio(true);

    setIsRecording(true);
    setRecordingStatus('connecting');
    setRecognizedText('');
    recordingStartTimeRef.current = Date.now();

    const params = {
      secretid: credentials.credentials.tmpSecretId,
      secretkey: credentials.credentials.tmpSecretKey,
      token: credentials.credentials.sessionToken,
      appid: credentials.appid,
      engine_model_type: '16k_zh',
      voice_format: 1,
      needvad: 1,
      filter_dirty: 1,
      filter_modal: 2,
      filter_punc: 0,
      convert_num_mode: 1,
    };

    try {
      const recognizer = new WebAudioSpeechRecognizer(params, true);
      webAudioSpeechRecognizerRef.current = recognizer;

      let resultText = '';

      recognizer.OnRecognitionStart = (res) => {
        console.log('开始识别', res);
      };

      recognizer.OnSentenceBegin = (res) => {
        console.log('一句话开始', res);
        setRecordingStatus('recording');
      };

      recognizer.OnRecognitionResultChange = (res) => {
        console.log('识别变化时', res);
        const currentText = `${resultText}${res.result.voice_text_str}`;
        setRecognizedText(currentText);
        setChatMessage(currentText);
      };

      recognizer.OnSentenceEnd = (res) => {
        console.log('一句话结束', res);
        resultText += res.result.voice_text_str;
        setRecognizedText(resultText);
        setChatMessage(resultText);
      };

      recognizer.OnRecognitionComplete = (res) => {
        console.log('识别结束', res);
        setRecordingStatus('');
        resultText = '';
      };

      recognizer.OnError = (res) => {
        console.log('识别失败', res);

        const recordingDuration = recordingStartTimeRef.current ? Date.now() - recordingStartTimeRef.current : 0;
        const minRecordingTime = 300;
        if (recordingDuration < minRecordingTime) {
          console.log('录音时间太短，忽略错误提示');
        } else {
          setError('语音识别失败，请重试。');
        }

        if (webAudioSpeechRecognizerRef.current) {
          webAudioSpeechRecognizerRef.current.destroyStream();
          webAudioSpeechRecognizerRef.current = null;
        }
        setIsRecording(false);
        setRecordingStatus('');
        setRecognizedText('');
        // 取消静音
        muteLocalAudio(false);
      };

      recognizer.OnRecorderStop = (res) => {
        console.log('录音停止', res);
        resultText = '';
      };

      recognizer.start();
    } catch (error) {
      console.error('启动语音识别失败:', error);
      setError('启动语音识别失败，请重试。');
      if (webAudioSpeechRecognizerRef.current) {
        webAudioSpeechRecognizerRef.current.destroyStream();
        webAudioSpeechRecognizerRef.current = null;
      }
      setIsRecording(false);
      setRecordingStatus('');
      setRecognizedText('');
      // 取消静音
      muteLocalAudio(false);
    }
  };

  // 停止语音识别
  const stopVoiceRecognition = async () => {
    if (webAudioSpeechRecognizerRef.current) {
      webAudioSpeechRecognizerRef.current.stop();
      webAudioSpeechRecognizerRef.current.destroyStream();
      webAudioSpeechRecognizerRef.current = null;
    }
    setIsRecording(false);
    setRecordingStatus('');
    setRecognizedText('');
    recordingStartTimeRef.current = null;
    // 取消静音
    await muteLocalAudio(false);
    
    // 如果有识别到的文字，自动发送
    if (recognizedText && recognizedText.trim()) {
      const message = recognizedText.trim();
      setChatMessage('');
      setRecognizedText('');
      // 调用发送消息函数
      handleSendChatMessageWithText(message);
    }
  };

  // 保存聊天记录到服务器
  const saveChatToServer = async (chatLog) => {
    try {
      await axios.post(`${hostIP}/api/saveChat`, {
        chat_log: chatLog,
        vaccine_type: chatType,
      }, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error saving chat:', error);
      // 静默失败，不显示错误提示，避免影响用户体验
    }
  };

  // 使用识别到的文字发送消息
  const handleSendChatMessageWithText = async (message) => {
    if (!message || message.trim() === '') {
      return;
    }

    setChatMessage('');
    setIsChatLoading(true);

    const payload = {
      message: message,
      history_messages: chatLog.slice(-5),
      vaccine_type: chatType
    };

    const updatedChatLog = [...chatLog, {role: 'user', content: message}];
    setChatLog(updatedChatLog);
    // 重置倒计时
    resetCountdown();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(hostIP + `/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': '123456',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let currentTextContent = '';

      const gptMessage = {role: 'gpt', content: ""};
      const newChatLog = [...updatedChatLog, gptMessage];
      setChatLog(newChatLog);

      while (true) {
        const {value, done} = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, {stream: true});

        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);

          if (line) {
            try {
              let jsonString = line;

              if (line.startsWith(':')) {
                continue;
              }

              if (line.startsWith('data: ')) {
                jsonString = line.substring(6);
              } else if (line.startsWith('data:')) {
                jsonString = line.substring(5);
              }

              if (!jsonString.trim()) {
                continue;
              }

              const jsonData = JSON.parse(jsonString);

              if (jsonData.type === 'text') {
                currentTextContent += jsonData.content;
                setDisplayedMessage(currentTextContent);
                const updatedGptMessage = {
                  role: 'gpt',
                  content: currentTextContent
                };
                const updatedChatLogWithMessage = [...newChatLog.slice(0, -1), updatedGptMessage];
                setChatLog(updatedChatLogWithMessage);
                // 收到第一个回复片段时重置倒计时
                if (currentTextContent === jsonData.content) {
                  resetCountdown();
                }
              }
            } catch (e) {
              console.error('解析JSON失败:', e, 'Line:', line);
            }
          }

          newlineIndex = buffer.indexOf('\n');
        }
      }

      clearTimeout(timeoutId);
      setIsChatLoading(false);

      const finalMessage = {
        role: 'gpt',
        content: currentTextContent
      };

      const finalChatLog = [...newChatLog.slice(0, -1), finalMessage];
      setChatLog(finalChatLog);
      setDisplayedMessage('');
      // 收到回复后重置倒计时
      resetCountdown();
      
      // 保存聊天记录到服务器
      saveChatToServer([
        {role: 'user', content: message},
        finalMessage
      ]);
      
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (e) {
      if (e.name === 'AbortError') {
        console.error('请求超时');
      } else {
        console.error(e);
      }
      setIsChatLoading(false);
    } finally {
      clearTimeout(timeoutId);
      setIsChatLoading(false);
    }
  };

  // 启动倒计时
  const startCountdown = () => {
    stopCountdown(); // 先清除之前的定时器
    
    countdownTimerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // 倒计时结束，自动退出
          stopCountdown();
          handleExitRoom();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 停止倒计时
  const stopCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  // 重置倒计时
  const resetCountdown = () => {
    setCountdown(300);
    if (!countdownTimerRef.current) {
      startCountdown();
    }
  };

  // 发送聊天消息
  const handleSendChatMessage = async () => {
    if (!chatMessage || chatMessage.trim() === '') {
      return;
    }

    // 如果正在录音，先停止录音
    if (isRecording) {
      await stopVoiceRecognition();
    }

    const message = chatMessage.trim();
    await handleSendChatMessageWithText(message);
  };

  // 取消排队
  const cancelQueue = async () => {
    if (!queueId) {
      // 如果没有 queueId，直接清除本地状态
      setQueueStatus('');
      setQueueId(null);
      clearQueueState();
      stopPolling();
      return;
    }

    try {
      // 调用服务器接口取消排队
      await axios.post(`${hostIP}/api/voicecall/cancel`, {
        queueId: queueId,
        userId: userId
      }, {
        withCredentials: true
      });

      // 停止轮询
      stopPolling();
      
      // 清除本地状态
      setQueueStatus('');
      setQueueId(null);
      clearQueueState();
      
      console.log('取消排队成功');
    } catch (error) {
      console.error('取消排队失败:', error);
      // 即使服务器取消失败，也清除本地状态和停止轮询
      stopPolling();
      setQueueStatus('');
      setQueueId(null);
      clearQueueState();
      // 可以选择是否显示错误提示
      // setError(`取消排队失败: ${error.response?.data?.message || error.message || '网络错误'}`);
    }
  };

  // 退出房间
  const handleExitRoom = async () => {
    const trtc = trtcRef.current;
    if (!trtc || !isInRoom) return;

    try {
      // 调用关闭接口中止对话
      try {
        await axios.post(`${hostIP}/api/voicecall/close`, {}, {
          withCredentials: true
        });
        console.log('关闭对话接口调用成功');
      } catch (closeError) {
        // 即使关闭接口失败，也继续退出房间流程
        console.error('调用关闭接口失败:', closeError);
      }

      // 停止语音识别
      if (webAudioSpeechRecognizerRef.current) {
        webAudioSpeechRecognizerRef.current.stop();
        webAudioSpeechRecognizerRef.current.destroyStream();
        webAudioSpeechRecognizerRef.current = null;
      }
      setIsRecording(false);
      setRecordingStatus('');
      setRecognizedText('');
      // 取消静音
      await muteLocalAudio(false);

      // 清理远端视频容器
      Object.keys(remoteVideoRefs.current).forEach(viewId => {
        const container = remoteVideoRefs.current[viewId];
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
      });
      remoteVideoRefs.current = {};

      // 退出房间
      await trtc.exitRoom();
      
      console.log('退出房间成功');
      setIsInRoom(false);
      setRoomId(null);
      setQueueStatus('');
      setQueueId(null);
      setRemoteUsers([]);
      setChatLog([]);
      setChatMessage('');
      setDisplayedMessage('');
      setError('');
      setSubtitleMessages([]); // 清除字幕消息
      subtitleMapRef.current = {}; // 清除字幕映射
      // 停止倒计时
      stopCountdown();
      setCountdown(300);
      // 清除排队状态
      clearQueueState();
      // 重置条款同意状态
      setHasAcceptedTerms(false);
    } catch (error) {
      console.error('退出房间失败:', error);
      setError(`退出房间失败: ${error.message || error}`);
    }
  };

  // 同意使用条款
  const handleAcceptTerms = () => {
    setHasAcceptedTerms(true);
    setError('');
  };

  // 格式化倒计时显示（MM:SS）
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={styles.container}>
      {/* 倒计时显示 - 仅在房间内显示 */}
      {isInRoom && (
        <div className={styles.countdownContainer}>
          <span className={styles.countdownText}>
            {formatCountdown(countdown)}
          </span>
        </div>
      )}

      {/* 挂断按钮 - 右上角 */}
      {isInRoom && (
        <button
          onClick={handleExitRoom}
          className={styles.hangupButton}
        >
          挂断
        </button>
      )}

      {!isInRoom && (
        <div className={styles.header}>
          <h1 className={styles.title}>语音通话</h1>
        </div>
      )}

      <div className={styles.content}>
        {!isInRoom ? (
          <>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>开始通话</h2>
              
              {/* 使用条款 */}
              {!hasAcceptedTerms && (
                <div className={styles.termsSection}>
                  <h3 className={styles.termsTitle}>使用条款</h3>
                  <div className={styles.termsContent}>
                    <div className={styles.termsItem}>
                      <strong>1. AI生成内容声明</strong>
                      <p>本服务提供的所有信息均由AI生成，仅供参考，不构成医疗建议或诊断依据。</p>
                    </div>
                    <div className={styles.termsItem}>
                      <strong>2. 非医疗建议</strong>
                      <p>本服务不能替代专业医疗咨询。如有健康问题，请咨询专业医疗机构或医生。</p>
                    </div>
                    <div className={styles.termsItem}>
                      <strong>3. 信息准确性</strong>
                      <p>我们不对AI生成信息的准确性、完整性或时效性作任何保证，用户需自行判断信息的适用性。</p>
                    </div>
                    <div className={styles.termsItem}>
                      <strong>4. 隐私保护</strong>
                      <p>您的通话内容将被用于提供服务和改进系统，我们将严格保护您的隐私信息。</p>
                    </div>
                    <div className={styles.termsItem}>
                      <strong>5. 使用责任</strong>
                      <p>用户应合理使用本服务，不得用于违法违规目的，因使用本服务产生的任何后果由用户自行承担。</p>
                    </div>
                  </div>
                  <button
                    onClick={handleAcceptTerms}
                    className={styles.termsAcceptButton}
                  >
                    我已阅读并同意
                  </button>
                </div>
              )}

              {queueStatus === 'waiting' && (
                <div className={styles.queueStatus}>
                  <div className={styles.loadingSpinner}></div>
                  <span>排队中，请稍候...</span>
                  <button
                    onClick={cancelQueue}
                    className={styles.cancelButton}
                  >
                    取消排队
                  </button>
                </div>
              )}
              <button
                onClick={startCall}
                className={styles.button}
                disabled={queueStatus === 'waiting' || !hasAcceptedTerms}
              >
                {queueStatus === 'waiting' ? '排队中...' : '开始通话'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.videoSection}>
              {/* 远端视频主显示区域 */}
              <div className={styles.mainVideoContainer}>
                <div id="remote-videos-container" className={styles.remoteVideosContainer}>
                  {/* {remoteUsers.length === 0 ? (
                    <div className={styles.noRemoteUsers}>等待其他用户加入...</div>
                  ) : ()} */}
                </div>
              </div>

              {/* 聊天消息显示区域 - 键盘弹出时隐藏 */}
              <div className={`${styles.chatContainer} ${!isKeyboardVisible ? styles.chatContainerAnimated : ''} ${isKeyboardVisible ? styles.chatContainerHidden : ''}`}>
                  <div className={styles.chatMessages}>
                    {chatLog.length === 0 ? (
                      <div className={styles.emptyChat}>开始对话...</div>
                    ) : (
                      chatLog.map((msg, index) => (
                        <div
                          key={index}
                          className={`${styles.chatMessage} ${styles.chatMessageAnimated} ${msg.role === 'user' ? styles.userMessage : styles.gptMessage}`}
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className={styles.chatMessageContent}>
                            {msg.role === 'gpt' && index === chatLog.length - 1 && displayedMessage ? (
                              displayedMessage
                            ) : (
                              msg.content
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    {isChatLoading && (
                      <div className={styles.chatLoading}>正在思考...</div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
              </div>

              {/* 输入框和发送按钮 - 键盘弹出时上移 */}
              <div 
                className={`${styles.chatInputContainer} ${isKeyboardVisible ? styles.chatInputContainerKeyboard : ''} ${styles.chatInputContainerAnimated}`}
                style={isKeyboardVisible && keyboardHeight > 0 ? { 
                  bottom: `${keyboardHeight + 10}px`,
                  position: 'fixed'
                } : {}}
              >
                <button
                  onClick={isRecording ? stopVoiceRecognition : startVoiceRecognition}
                  className={`${styles.voiceButton} ${isRecording ? styles.voiceButtonActive : ''}`}
                  disabled={isChatLoading}
                  title={isRecording ? '点击停止录音' : '点击开始语音输入'}
                >
                  <span className={styles.voiceButtonIcon}>
                    {recordingStatus === 'connecting' && '🔌'}
                    {recordingStatus === 'recording' && '🎙️'}
                    {!recordingStatus && '🎙️'}
                  </span>
                  <span className={styles.voiceButtonText}>
                    {isRecording ? '停止录音' : '开始录音'}
                  </span>
                </button>
                <input
                  ref={chatInputRef}
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isChatLoading) {
                      e.preventDefault();
                      handleSendChatMessage();
                    }
                  }}
                  placeholder={isRecording ? '正在录音...' : '输入消息...'}
                  className={styles.chatInput}
                  disabled={isChatLoading || isRecording}
                />
                <button
                  onClick={handleSendChatMessage}
                  className={`${styles.sendButton} ${styles.sendButtonAnimated}`}
                  disabled={isChatLoading || !chatMessage.trim()}
                >
                  发送
                </button>
              </div>

              {error && (
                <div className={styles.errorMessage}>{error}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceCall;
